import { useEffect, useRef, useState } from 'react';

import { useScrollRegion } from './ScrollRegionContext';

/**
 * Whether an element still needs the room it is holding open beneath itself.
 *
 * Scrolling stops at the end of the content, so an element can only reach the top of the region if
 * there is a region's worth of content below it - which usually means the element reserving that
 * much height itself. This says when the reserve has been spent and can go.
 *
 * **It latches, and only lets go once the element is out of sight.** Taking a region's worth of
 * height away while the reader is looking at it pulls the content out from under them, so the
 * answer waits until the element has left the box altogether. Put the returned `ref` on the element
 * and give `key` whatever identifies it - a new one starts over.
 */
export const useScrollRegionReserve = <ElementType extends HTMLElement>({
  key,
  tolerance = 8,
}: {
  key: string | null;
  /** How near the region's top counts as having arrived there. */
  tolerance?: number;
}) => {
  const { onMeasure, regionRef } = useScrollRegion();

  const ref = useRef<ElementType | null>(null);
  const [hasRoom, setHasRoom] = useState(true);

  const hasArrived = useRef(false);

  useEffect(() => {
    hasArrived.current = false;
    setHasRoom(true);
  }, [key]);

  useEffect(() => {
    if (!hasRoom) return;

    /*
      Both edges are the region's, not the window's: the region is the box the element scrolls
      within, and it rarely starts at the top of the page.
    */
    const measure = () => {
      const element = ref.current;
      const region = regionRef.current;

      if (!element || !region) return;

      const { top } = element.getBoundingClientRect();
      const { bottom: regionBottom, top: regionTop } =
        region.getBoundingClientRect();

      if (top - regionTop <= tolerance) hasArrived.current = true;

      if (hasArrived.current && top > regionBottom) setHasRoom(false);
    };

    // Once up front: the region's own loop started when it mounted, which is before this element did.
    measure();

    /*
      The region measures once for everything watching it and calls this, rather than this attaching
      listeners of its own to the same box - it also decides when the layout is too busy to be
      measured at all.
    */
    return onMeasure(measure);
  }, [hasRoom, onMeasure, regionRef, tolerance]);

  return { hasRoom, ref };
};
